'use client';

import * as VKID from '@vkid/sdk';
import { useEffect, useState } from 'react';
import { setUser } from '@/app/lib/store/user-slice';
import { useAppDispatch, useAppSelector } from '@/app/lib/store/hooks';
import { useRouter } from 'next/navigation';
import { getCookie } from 'cookies-next/client';

import SignInDialogButton from './sign-in-dialog-button';

interface SignInContainerProps {
  onClose: () => void;
}

export default function SignInDialogButtonContainer({
  onClose
}: SignInContainerProps) {
  const [codeChallenge, setCodeChallenge] = useState<string | null>(null);

  const navigateTo = useAppSelector(state => state.returnTo.pathname);

  const dispatch = useAppDispatch();
  const router = useRouter();

  useEffect(() => {
    fetch('/api/auth/code-verifier', { method: 'POST' })
      .then(res => res.json())
      .then(data => {
        if (data.codeChallenge) {
          setCodeChallenge(data.codeChallenge);
        } else {
          console.error('Failed to fetch code challenge');
        }
      })
      .catch(error => console.error('Error fetching code challenge:', error));
  }, []);

  useEffect(() => {
    if (codeChallenge) {
      VKID.Config.init({
        app: Number(process.env.NEXT_PUBLIC_VK_CLIENT_ID!),
        redirectUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/api/auth/vk/callback`,
        state: 'dj29fnsadjsd82',
        codeChallenge: codeChallenge,
        responseMode: VKID.ConfigResponseMode.Callback,
        source: VKID.ConfigSource.LOWCODE,
        scope: 'email'
      });
    }
  }, [codeChallenge]);

  function vkidOnError(error: unknown) {
    console.error('VKID Auth Error:', error);
  }

  useEffect(() => {
    const theme = getCookie('theme');
    const oneTap = new VKID.OneTap();
    const container = document.getElementById('VkIdSdkOneTap');
    if (container) {
      oneTap
        .render({
          container: container,
          showAlternativeLogin: true,
          styles: {
            borderRadius: 50,
            height: 32
          },
          scheme: theme === 'light' ? VKID.Scheme.LIGHT : VKID.Scheme.DARK,
          lang: VKID.Languages.RUS
        })
        .on(VKID.WidgetEvents.ERROR, vkidOnError)
        .on(
          VKID.OneTapInternalEvents.LOGIN_SUCCESS,
          async function (payload: unknown) {
            try {
              const response = await fetch('/api/auth/vk/callback', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
              });

              if (response.ok) {
                const data = await response.json();
                onClose();
                dispatch(setUser(data.user));
              } else {
                const { error } = await response.json();
                console.error(error);
              }
            } catch (error) {
              vkidOnError(error);
            }
          }
        );
    }
  }, [dispatch, router, navigateTo, onClose]);

  return <SignInDialogButton containerId='VkIdSdkOneTap' />;
}
